import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, RefreshCw, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/csv";
import { chunkedBulkUpdate } from "@/lib/chunkedImport";

export default function BulkUpdateDialog({ open, onOpenChange, onUpdated }) {
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null); // {job_id, total, processed, updated, not_found, progress, status, done, not_found_skus}
  const [starting, setStarting] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => {
    setFile(null); setJob(null); setStarting(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (inputRef.current) inputRef.current.value = "";
  };
  const close = () => { reset(); onOpenChange(false); };

  const downloadTemplate = async (fmt) => {
    try {
      const { data } = await api.get(`/admin/books/bulk-update-template?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "csv" ? "bulk-update-template.csv" : "bulk-update-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPoll = (job_id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/books/bulk-update/${job_id}`);
        setJob(data);
        if (data.done) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (data.status === "done") {
            toast.success(`Updated ${data.updated} of ${data.total} rows.`);
            onUpdated?.();
          } else if (data.status === "error") {
            toast.error(data.error || "Bulk update failed");
          }
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.error("Lost connection to the job.");
      }
    }, 500);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStarting(true);

    // Aggregated job state that mirrors the shape used by the rest of the dialog.
    setJob({
      job_id: "chunked",
      total: 0, processed: 0, updated: 0, not_found: 0, smart_recategorized: 0,
      progress: 0, status: "processing", done: false, not_found_skus: [],
    });

    try {
      await chunkedBulkUpdate(f, {
        onSplit: ({ totalRows, chunkCount, chunkSize }) => {
          setStarting(false);
          setJob((prev) => ({ ...prev, total: totalRows, chunkCount, chunkSize, status: "processing" }));
          toast(`Split into ${chunkCount} chunk${chunkCount === 1 ? "" : "s"} of ~${chunkSize.toLocaleString()} rows. Uploading…`);
        },
        onChunkQueued: ({ index, total }) => {
          setJob((prev) => ({ ...prev, uploadedChunks: index + 1, chunkCount: total }));
        },
        onProgress: (p) => {
          const pct = p.total > 0 ? Math.round((p.processed / p.total) * 100) : 0;
          setJob((prev) => ({
            ...prev,
            total: p.total,
            processed: p.processed,
            updated: p.updated,
            not_found: p.not_found,
            smart_recategorized: p.smart_recategorized,
            progress: pct,
            status: "processing",
            chunkStates: p.chunkStates,
          }));
        },
        onDone: ({ updated, not_found, smart_recategorized, notFoundSkus, chunkStates }) => {
          setJob((prev) => ({
            ...prev,
            updated, not_found, smart_recategorized,
            not_found_skus: notFoundSkus || [],
            chunkStates,
            progress: 100,
            status: "done",
            done: true,
          }));
          toast.success(`${updated.toLocaleString()} rows updated (${not_found} not found)`);
        },
        onError: (msg) => {
          setJob((prev) => ({ ...prev, status: "error", error: msg, done: true }));
          setStarting(false);
          toast.error(msg);
        },
      });
    } catch (err) {
      toast.error(err.message || "Could not start bulk update");
      setStarting(false);
    }
  };

  const progressPct = job ? Math.min(100, Math.max(0, Math.round(job.progress || 0))) : 0;
  const done = job?.done;
  const okDone = job?.status === "done";

  const downloadReport = async () => {
    const chunks = job?.chunkStates || [];
    const jobIds = chunks.map((c) => c.jobId).filter(Boolean);
    if (jobIds.length === 0) {
      toast.error("No completed chunks to report");
      return;
    }
    try {
      // Fetch each chunk's CSV report and concatenate (skip header on all but first)
      const parts = [];
      for (let i = 0; i < jobIds.length; i++) {
        const resp = await api.get(`/books/bulk-update/${jobIds[i]}/report`, { responseType: "text" });
        if (i === 0) parts.push(resp.data);
        else parts.push(resp.data.split(/\r?\n/).slice(1).join("\n"));
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const blob = new Blob([parts.join("\n")], { type: "text/csv" });
      downloadBlob(blob, `bulk-update-report-${stamp}.csv`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download report");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-3xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="bulk-update-dialog"
      >
        <DialogHeader className="p-6 border-b border-[#E5E1D8]">
          <DialogTitle className="font-serif text-3xl text-[#1A1714]">Bulk update inventory (CSV or Excel)</DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            Columns: SKU (required) · ISBN · Title · Item photo URL · Author · Publisher · BISAC · Pages · Format · Language · Condition · Description · Price · Quantity
          </DialogDescription>
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#7A2021] mt-1">
            Match by SKU. Only fill columns you want to change — blank cells keep existing values. BISAC column auto-recategorizes.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("xlsx")}
              data-testid="bulk-update-template-xlsx"
              className="rounded-none border-[#7A2021] text-[#7A2021] hover:bg-[#7A2021] hover:text-[#F9F6F0] font-mono text-[10px] uppercase tracking-editorial"
            >
              <Download size={12} className="mr-1.5" /> Download Excel template
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("csv")}
              data-testid="bulk-update-template-csv"
              className="rounded-none border-[#E5E1D8] text-[#1A1714]/70 hover:bg-[#F0EBE0] font-mono text-[10px] uppercase tracking-editorial"
            >
              <Download size={12} className="mr-1.5" /> Download CSV template
            </Button>
          </div>
        </DialogHeader>

        {!job && (
          <div className="p-10 flex-1 flex flex-col items-center justify-center">
            <label
              htmlFor="bulk-update-input"
              className="cursor-pointer border-2 border-dashed border-[#E5E1D8] hover:border-[#1A1714] w-full max-w-xl p-16 text-center transition-colors"
              data-testid="bulk-update-drop"
            >
              <Upload className="mx-auto text-[#1A1714]/40" size={40} />
              <p className="mt-4 font-serif text-2xl text-[#1A1714]">Drop a CSV or Excel file, or click to choose</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">
                Accepts .csv, .xlsx · Required column: SKU · Any of Title, Author, ISBN, Publisher, BISAC, Price, Quantity, Format, Description, Language, Condition, Cover, Pages
              </p>
              <input
                id="bulk-update-input"
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={onFile}
                className="hidden"
                data-testid="bulk-update-file-input"
              />
            </label>
            {starting && <p className="mt-6 font-serif italic text-lg text-[#1A1714]/60">Queueing job…</p>}
          </div>
        )}

        {job && (
          <div className="p-6 flex-1 flex flex-col gap-6" data-testid="bulk-update-status">
            <div>
              <div className="flex justify-between items-baseline">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                  {done ? (okDone ? "Complete" : "Failed") : "Processing"} · {file?.name}
                </p>
                <p className="font-mono text-xs text-[#1A1714]" data-testid="bulk-update-progress-label">
                  {job.processed?.toLocaleString?.() ?? job.processed} / {job.total?.toLocaleString?.() ?? job.total}
                </p>
              </div>
              <Progress
                value={progressPct}
                className="mt-3 h-3 bg-[#E5E1D8] rounded-none [&>div]:bg-[#7A2021] [&>div]:rounded-none"
                data-testid="bulk-update-progress"
              />
              <div className="mt-2 flex justify-between items-baseline font-mono text-[10px] uppercase tracking-editorial">
                <span className="text-[#1A1714]/50">Batches of {job.batch_size ?? 5000}</span>
                <span className="text-[#7A2021]" data-testid="bulk-update-progress-pct">{progressPct}%</span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 border-y border-[#E5E1D8] py-6">
              <Stat label="Rows in file" value={job.total} testId="bulk-stat-total" />
              <Stat label="Updated" value={job.updated} accent testId="bulk-stat-updated" />
              <Stat label="Auto-categorized" value={job.smart_recategorized || 0} testId="bulk-stat-smart" />
              <Stat label="SKUs not found" value={job.not_found} testId="bulk-stat-notfound" />
            </div>

            {job.not_found_skus?.length > 0 && (
              <div data-testid="bulk-not-found-list">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#7A2021] mb-2 flex items-center gap-1">
                  <AlertTriangle size={12} /> Not-found SKUs (showing first {job.not_found_skus.length})
                </p>
                <div className="max-h-32 overflow-y-auto border border-[#E5E1D8] p-3 font-mono text-xs text-[#1A1714]/80">
                  {job.not_found_skus.join(", ")}
                </div>
              </div>
            )}

            <div className="flex-1" />

            <div className="flex justify-between items-center gap-3 border-t border-[#E5E1D8] pt-5">
              {!done ? (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 flex items-center gap-2">
                    <RefreshCw size={12} className="animate-spin" /> Running on the server — safe to leave this open
                  </p>
                  <Button
                    disabled
                    className="rounded-none bg-[#1A1714] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial opacity-60"
                  >
                    Processing…
                  </Button>
                </>
              ) : (
                <>
                  <p className={`font-serif text-lg flex items-center gap-2 ${okDone ? "text-[#1A1714]" : "text-[#7A2021]"}`}>
                    {okDone ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                    {okDone
                      ? <>Done. <em className="text-[#7A2021]">{job.updated}</em> updated · <em>{job.not_found}</em> not found</>
                      : <>Job failed: {job.error}</>}
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    {okDone && (
                      <Button
                        onClick={downloadReport}
                        variant="outline"
                        className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                        data-testid="bulk-update-download-report"
                      >
                        <Download size={14} className="mr-2" /> Download report
                      </Button>
                    )}
                    <Button
                      onClick={reset}
                      variant="outline"
                      className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                      data-testid="bulk-update-again"
                    >
                      Update another
                    </Button>
                    <Button
                      onClick={close}
                      className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial"
                      data-testid="bulk-update-close"
                    >
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">{label}</p>
      <p className={`font-serif text-3xl mt-1 ${accent ? "text-[#7A2021]" : "text-[#1A1714]"}`} data-testid={testId}>
        {(value ?? 0).toLocaleString()}
      </p>
    </div>
  );
}
